import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import StatusBadge from '../StatusBadge.jsx';
import Modal from '../Modal.jsx';
import KeyValueEditor from '../KeyValueEditor.jsx';
import MaskedParamsEditor from '../MaskedParamsEditor.jsx';
import FloorEditor from './FloorRow.jsx';
import HouseAdEditor from './HouseAdEditor.jsx';

const MEDIA_TYPE_KEYS = ['banner', 'video', 'native'];

export default function PlacementsTab({ publisherId }) {
  const toast = useToast();
  const [placements, setPlacements] = useState(null);
  const [floors, setFloors] = useState(null);
  const [houseAds, setHouseAds] = useState([]);
  const [demandPartners, setDemandPartners] = useState([]);
  const [bidders, setBidders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [pl, fl, ha, dp, bd] = await Promise.all([
        api.listPlacements(publisherId),
        api.listPublisherFloors(publisherId),
        api.listHouseAds(publisherId).catch(() => []),
        api.listDemandPartners().catch(() => []),
        api.listBidders().catch(() => []),
      ]);
      setPlacements(pl);
      setFloors(fl);
      setHouseAds(ha);
      setDemandPartners(dp);
      setBidders(bd);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load placements.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [publisherId]);

  const defaultFloors = (floors || []).filter((f) => !f.placement_id);
  const defaultHouseAds = (houseAds || []).filter((h) => !h.placement_id);

  async function deletePlacement(placement) {
    if (!window.confirm(`Delete placement "${placement.ad_unit_code}"? This cannot be undone.`)) return;
    try {
      await api.deletePlacement(placement.id);
      toast.success('Placement deleted.');
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete placement.');
    }
  }

  if (loading) return <LoadingState label="Loading placements" />;
  if (error) return <EmptyState title="Couldn't load placements" message={error} />;

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Publisher-wide default floors</h2>
          <span className="card__hint">Used when a placement has no floor of its own</span>
        </div>
        <FloorEditor
          floors={defaultFloors}
          onSave={async (mediaType, body) => {
            await api.putPublisherFloor(publisherId, mediaType, body);
            load();
          }}
        />
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="card__title">House ads — publisher-wide default</h2>
          <span className="card__hint">
            Served, one per format, when a real auction returns zero bids and this placement has
            no house ad of its own
          </span>
        </div>
        <div className="card__padded">
          <HouseAdEditor
            houseAds={defaultHouseAds}
            emptyHint="No fallback configured — a no-fill currently renders as a blank slot for every placement without its own override below."
            onSave={async (format, body) => {
              await api.putPublisherHouseAd(publisherId, format, body);
              load();
            }}
            onDelete={async (format) => {
              await api.deletePublisherHouseAd(publisherId, format);
              load();
            }}
          />
        </div>
      </section>

      <section className="card card--flush">
        <div className="card__header card__header--padded">
          <h2 className="card__title">Placements</h2>
          <button className="btn btn--primary btn--sm" onClick={() => setShowCreate(true)}>
            + New placement
          </button>
        </div>

        {(!placements || placements.length === 0) ? (
          <div className="card__padded">
            <EmptyState
              title="No placements yet"
              message="Create your first ad placement for this publisher."
              action={
                <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
                  + New placement
                </button>
              }
            />
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ad unit code</th>
                  <th>Media types</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {placements.map((p) => (
                  <PlacementRow
                    key={p.id}
                    placement={p}
                    expanded={expandedId === p.id}
                    onToggleExpand={() => setExpandedId(expandedId === p.id ? null : p.id)}
                    onEdit={() => setEditingPlacement(p)}
                    onDelete={() => deletePlacement(p)}
                    floors={(floors || []).filter((f) => f.placement_id === p.id)}
                    houseAds={(houseAds || []).filter((h) => h.placement_id === p.id)}
                    demandPartners={demandPartners}
                    bidders={bidders}
                    onFloorsChanged={load}
                    onHouseAdChanged={load}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showCreate && (
        <PlacementModal
          title="New placement"
          onClose={() => setShowCreate(false)}
          onSave={async ({ ad_unit_code, media_types }) => {
            await api.createPlacement(publisherId, { ad_unit_code, media_types });
            toast.success('Placement created.');
            setShowCreate(false);
            load();
          }}
        />
      )}

      {editingPlacement && (
        <PlacementModal
          title={`Edit ${editingPlacement.ad_unit_code}`}
          initial={editingPlacement}
          onClose={() => setEditingPlacement(null)}
          onSave={async ({ ad_unit_code, media_types }) => {
            await api.patchPlacement(editingPlacement.id, { ad_unit_code, media_types });
            toast.success('Placement updated.');
            setEditingPlacement(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function PlacementRow({ placement, expanded, onToggleExpand, onEdit, onDelete, floors, houseAds, demandPartners, bidders, onFloorsChanged, onHouseAdChanged }) {
  return (
    <>
      <tr className="data-table__row--expandable" onClick={onToggleExpand}>
        <td className="cell-title">
          <span className={`disclosure${expanded ? ' disclosure--open' : ''}`} aria-hidden="true">
            &#9656;
          </span>
          {placement.ad_unit_code}
        </td>
        <td>{summarizeMediaTypes(placement.media_types)}</td>
        <td>
          <StatusBadge status={placement.status} />
        </td>
        <td onClick={(e) => e.stopPropagation()}>
          <div className="row-actions">
            <button className="btn btn--ghost btn--sm" onClick={onEdit}>
              Edit
            </button>
            <button className="btn btn--danger-ghost btn--sm" onClick={onDelete}>
              Delete
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="data-table__expansion-row">
          <td colSpan={4}>
            <PlacementExpansion
              placement={placement}
              floors={floors}
              houseAds={houseAds}
              demandPartners={demandPartners}
              bidders={bidders}
              onFloorsChanged={onFloorsChanged}
              onHouseAdChanged={onHouseAdChanged}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function PlacementExpansion({ placement, floors, houseAds, demandPartners, bidders, onFloorsChanged, onHouseAdChanged }) {
  const toast = useToast();

  // Compatible-by-media-type first (small correctness fix: a video placement shouldn't default
  // to a banner-only partner) -- but never hide a partner entirely, since a network-default
  // credential could still legitimately apply across media types operators want to override.
  const placementMediaTypes = Object.keys(placement.media_types || {});
  const compatiblePartners = demandPartners.filter((dp) => (dp.media_types || []).some((mt) => placementMediaTypes.includes(mt)));
  const orderedPartners = compatiblePartners.length
    ? [...compatiblePartners, ...demandPartners.filter((dp) => !compatiblePartners.includes(dp))]
    : demandPartners;

  const [selectedPartner, setSelectedPartner] = useState(orderedPartners[0]?.code || '');
  const [overrides, setOverrides] = useState(null); // this placement's existing demand-partner-param overrides (key names only)
  const [savingParams, setSavingParams] = useState(false);

  const [selectedBidder, setSelectedBidder] = useState(bidders[0]?.code || '');
  const [bidderParams, setBidderParams] = useState({});
  const [savingBidderParams, setSavingBidderParams] = useState(false);

  async function loadOverrides() {
    try {
      setOverrides(await api.listPlacementDemandPartnerParams(placement.id));
    } catch {
      setOverrides([]);
    }
  }

  useEffect(() => {
    loadOverrides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement.id]);

  const selectedPartnerObj = demandPartners.find((dp) => dp.code === selectedPartner);
  const selectedIsIncompatible = selectedPartnerObj && placementMediaTypes.length > 0 &&
    !(selectedPartnerObj.media_types || []).some((mt) => placementMediaTypes.includes(mt));
  const existingOverride = (overrides || []).find((o) => o.code === selectedPartner);

  async function saveOverride(params) {
    if (!selectedPartner) {
      toast.error('Select a demand partner first.');
      return;
    }
    setSavingParams(true);
    try {
      await api.putPlacementDemandPartnerParams(placement.id, selectedPartner, params);
      toast.success(`${selectedPartner} params saved for this placement.`);
      await loadOverrides();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save override.');
      throw err;
    } finally {
      setSavingParams(false);
    }
  }

  async function saveBidderOverride() {
    if (!selectedBidder) {
      toast.error('Select a bidder first.');
      return;
    }
    setSavingBidderParams(true);
    try {
      await api.putPlacementBidderParams(placement.id, selectedBidder, bidderParams);
      toast.success(`${selectedBidder} params saved for this placement.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save override.');
    } finally {
      setSavingBidderParams(false);
    }
  }

  return (
    <div className="placement-expansion placement-expansion--wide">
      <div className="placement-expansion__block">
        <h3 className="placement-expansion__heading">Floor prices</h3>
        <FloorEditor
          dense
          floors={floors}
          onSave={async (mediaType, body) => {
            await api.putPlacementFloor(placement.id, mediaType, body);
            onFloorsChanged();
          }}
        />
      </div>
      <div className="placement-expansion__block">
        <h3 className="placement-expansion__heading">House ad fallback</h3>
        <HouseAdEditor
          houseAds={houseAds}
          emptyHint="No override for this placement — the publisher-wide default above (if any) applies instead, per format."
          onSave={async (format, body) => {
            await api.putPlacementHouseAd(placement.id, format, body);
            onHouseAdChanged();
          }}
          onDelete={async (format) => {
            await api.deletePlacementHouseAd(placement.id, format);
            onHouseAdChanged();
          }}
        />
      </div>
      <div className="placement-expansion__block">
        <h3 className="placement-expansion__heading">Demand partner param override</h3>
        {demandPartners.length === 0 ? (
          <p className="card__hint">No demand partners in the network catalog yet.</p>
        ) : (
          <>
            <select
              className="placement-expansion__partner-select"
              value={selectedPartner}
              onChange={(e) => setSelectedPartner(e.target.value)}
            >
              {orderedPartners.map((dp) => (
                <option key={dp.code} value={dp.code}>
                  {dp.name} ({dp.code}){(dp.media_types || []).some((mt) => placementMediaTypes.includes(mt)) ? '' : ' — different media type'}
                </option>
              ))}
            </select>
            {selectedIsIncompatible && (
              <p className="card__hint card__hint--warning">
                {selectedPartnerObj.name} supports {(selectedPartnerObj.media_types || []).join(', ') || 'no'} —
                this placement is {placementMediaTypes.join(', ') || 'unset'}. An override here won't be used unless that changes.
              </p>
            )}
            {overrides === null ? (
              <p className="card__hint">Loading current overrides…</p>
            ) : (
              <MaskedParamsEditor
                key={selectedPartner}
                existingKeys={existingOverride?.paramKeys || []}
                onSave={saveOverride}
                disabled={savingParams}
                emptyHint="No override set for this partner on this placement — the publisher-level or network-default credential applies instead."
              />
            )}
          </>
        )}
      </div>
      <div className="placement-expansion__block">
        <h3 className="placement-expansion__heading">Client-side bidder param override</h3>
        {bidders.length === 0 ? (
          <p className="card__hint">No bidders in the catalog yet.</p>
        ) : (
          <>
            <select
              className="placement-expansion__partner-select"
              value={selectedBidder}
              onChange={(e) => {
                setSelectedBidder(e.target.value);
                setBidderParams({});
              }}
            >
              {bidders.map((b) => (
                <option key={b.code} value={b.code}>
                  {b.name} ({b.code})
                </option>
              ))}
            </select>
            <KeyValueEditor value={bidderParams} onChange={setBidderParams} disabled={savingBidderParams} />
            <button className="btn btn--ghost btn--sm" onClick={saveBidderOverride} disabled={savingBidderParams}>
              {savingBidderParams ? 'Saving…' : 'Save override'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function summarizeMediaTypes(mediaTypes) {
  const keys = Object.keys(mediaTypes || {});
  if (!keys.length) return '—';
  return keys
    .map((k) => {
      if (k === 'banner' && mediaTypes.banner?.sizes?.length) {
        return `banner (${mediaTypes.banner.sizes.map((s) => s.join('x')).join(', ')})`;
      }
      return k;
    })
    .join(', ');
}

function PlacementModal({ title, initial, onClose, onSave }) {
  const toast = useToast();
  const [adUnitCode, setAdUnitCode] = useState(initial?.ad_unit_code || '');
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const keys = Object.keys(initial?.media_types || {});
    return keys.length ? keys : ['banner'];
  });
  const [sizes, setSizes] = useState(() => initial?.media_types?.banner?.sizes || [[300, 250]]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  function toggleType(type) {
    setSelectedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  }

  function updateSize(i, dim, value) {
    setSizes((prev) => prev.map((s, idx) => (idx === i ? (dim === 'w' ? [Number(value) || 0, s[1]] : [s[0], Number(value) || 0]) : s)));
  }

  function addSize() {
    setSizes((prev) => [...prev, [300, 250]]);
  }

  function removeSize(i) {
    setSizes((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    if (!adUnitCode.trim()) {
      setFormError('Ad unit code is required.');
      return;
    }
    if (!selectedTypes.length) {
      setFormError('Select at least one media type.');
      return;
    }
    const media_types = {};
    selectedTypes.forEach((t) => {
      if (t === 'banner') media_types.banner = { sizes: sizes.filter((s) => s[0] > 0 && s[1] > 0) };
      else media_types[t] = {};
    });
    setSaving(true);
    try {
      await onSave({ ad_unit_code: adUnitCode.trim(), media_types });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not save placement.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={title} onClose={onClose} width={520}>
      <form onSubmit={handleSubmit} className="form">
        <label className="field">
          <span className="field__label">Ad unit code</span>
          <input value={adUnitCode} onChange={(e) => setAdUnitCode(e.target.value)} placeholder="div-gpt-ad-leaderboard" />
        </label>

        <div className="field">
          <span className="field__label">Media types</span>
          <div className="checkbox-row">
            {MEDIA_TYPE_KEYS.map((t) => (
              <label key={t} className="field field--checkbox field--checkbox-inline">
                <input type="checkbox" checked={selectedTypes.includes(t)} onChange={() => toggleType(t)} />
                <span>{t}</span>
              </label>
            ))}
          </div>
        </div>

        {selectedTypes.includes('banner') && (
          <div className="field">
            <span className="field__label">Banner sizes</span>
            {sizes.map((s, i) => (
              <div className="size-row" key={i}>
                <input type="number" min="1" value={s[0]} onChange={(e) => updateSize(i, 'w', e.target.value)} />
                <span>&times;</span>
                <input type="number" min="1" value={s[1]} onChange={(e) => updateSize(i, 'h', e.target.value)} />
                <button type="button" className="kv-editor__remove" onClick={() => removeSize(i)} aria-label="Remove size">
                  &times;
                </button>
              </div>
            ))}
            <button type="button" className="btn btn--ghost btn--sm" onClick={addSize}>
              + Add size
            </button>
          </div>
        )}

        {formError && <div className="form-error">{formError}</div>}

        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save placement'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
